/**
 * ChartboostIntegrationPage — server-to-server event intake form + stats +
 * recent event feed for the Chartboost gaming ad network integration.
 * BRAND GREEN ONLY. No other color families.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useChartboostIntegrationCreate,
	useChartboostIntegrationList,
	useChartboostIntegrationStats,
} from "./hooks";
import type { ChartboostIntegrationRow } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT = INPUT;
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";

const EVENT_OPTIONS = ["install", "purchase", "level_up", "session_start"];

function fmtTs(sec: number): string {
	if (!Number.isFinite(sec) || sec <= 0) return "-";
	try {
 return (
 new Date(sec * 1000).toISOString().replace("T","").slice(0, 19) + "Z"
 );
	} catch {
 return "-";
	}
}

function maskId(s: string): string {
	if (!s) return "";
	if (s.length <= 10) return s;
	return s.slice(0, 6) + "..." + s.slice(-4);
}

function IconChart() {
	return (
 <svg
 viewBox="0 0 24 24"
 aria-hidden="true"
 className="h-5 w-5"
 fill="none"
 stroke="currentColor"
 strokeWidth="2"
 strokeLinecap="round"
 strokeLinejoin="round"
 >
 <path d="M3 3v18h18" />
 <path d="M7 14l4-4 3 3 5-6" />
 </svg>
	);
}

export function ChartboostIntegrationPage() {
	const createMut = useChartboostIntegrationCreate();
	const listQ = useChartboostIntegrationList();
	const statsQ = useChartboostIntegrationStats();

	const [clickId, setClickId] = useState("");
	const [eventName, setEventName] = useState("install");
	const [appId, setAppId] = useState("");
	const [ifa, setIfa] = useState("");
	const [country, setCountry] = useState("");
	const [saved, setSaved] = useState(false);

	const handleSubmit = (e: FormEvent) => {
 e.preventDefault();
 setSaved(false);
 const payload: {
 chartboost_click_id: string;
 event_name: string;
 network_app_id: string;
 ifa?: string;
 country?: string;
 } = {
 chartboost_click_id: clickId.trim(),
 event_name: eventName.trim(),
 network_app_id: appId.trim(),
 };
 const ifaTrim = ifa.trim();
 const countryTrim = country.trim();
 if (ifaTrim) payload.ifa = ifaTrim;
 if (countryTrim) payload.country = countryTrim.toUpperCase();

 createMut.mutate(payload, {
 onSuccess: () => {
 setSaved(true);
 setClickId("");
 setIfa("");
 },
 });
	};

	const items: ChartboostIntegrationRow[] = listQ.data?.items ?? [];

	const summary = useMemo(() => {
 const total = statsQ.data?.total ?? 0;
 const byEvent = statsQ.data?.by_event ?? {};
 const installs = byEvent["install"] ?? 0;
 const purchases = byEvent["purchase"] ?? 0;
 const lastAt = items.reduce(
 (acc, i) => Math.max(acc, i.created_at ?? 0),
 0,
 );
 return { total, installs, purchases, lastAt };
	}, [statsQ.data, items]);

	return (
 <main
 data-testid="chartboost-integration-page"
 className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <div className="flex items-center gap-2 text-green-700">
 <IconChart />
 <span className="text-xs font-semibold uppercase tracking-wide">
 Integration partner
 </span>
 </div>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 Chartboost Integration
 </h1>
 <p className="text-sm text-zinc-600">
 Forward install, purchase, and level_up postbacks from the Chartboost
 gaming ad network into Trackerity. Events are deduped on the click id
 and event name so the network can safely retry.
 </p>
 </header>

 <section
 aria-label="event stats"
 data-testid="chartboost-integration-status"
 className={`${CARD} grid grid-cols-1 gap-4 sm:grid-cols-4`}
 >
 <div className="flex flex-col gap-1">
 <span className="text-xs uppercase tracking-wide text-zinc-500">
 Total events
 </span>
 <span
 className="text-2xl font-semibold text-zinc-900"
 data-testid="chartboost-integration-status-total"
 >
 {summary.total}
 </span>
 </div>
 <div className="flex flex-col gap-1">
 <span className="text-xs uppercase tracking-wide text-zinc-500">
 Installs
 </span>
 <span
 className="text-2xl font-semibold text-green-700"
 data-testid="chartboost-integration-status-installs"
 >
 {summary.installs}
 </span>
 </div>
 <div className="flex flex-col gap-1">
 <span className="text-xs uppercase tracking-wide text-zinc-500">
 Purchases
 </span>
 <span
 className="text-2xl font-semibold text-green-700"
 data-testid="chartboost-integration-status-purchases"
 >
 {summary.purchases}
 </span>
 </div>
 <div className="flex flex-col gap-1">
 <span className="text-xs uppercase tracking-wide text-zinc-500">
 Last received
 </span>
 <span
 className="text-sm font-medium text-zinc-900"
 data-testid="chartboost-integration-status-last"
 >
 {fmtTs(summary.lastAt)}
 </span>
 </div>
 </section>

 <form
 onSubmit={handleSubmit}
 className={`${CARD} flex flex-col gap-4`}
 data-testid="chartboost-integration-form"
 aria-label="chartboost-integration form"
 >
 <div className="flex flex-col gap-1">
 <h2 className="text-green-700 font-semibold text-lg font-semibold text-zinc-900">
 Record postback event
 </h2>
 <p className="text-xs text-zinc-500">
 Use this form to forward a server-to-server event from the
 Chartboost network. Repeat calls with the same click id and event
 name are idempotent.
 </p>
 </div>

 <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
 <div className="flex flex-col gap-1.5">
 <label
 htmlFor="chartboost-integration-click-id"
 className={LABEL}
 >
 Click id
 </label>
 <input
 id="chartboost-integration-click-id"
 type="text"
 required
 maxLength={128}
 placeholder="cb_click_abc123"
 className={INPUT}
 value={clickId}
 onChange={(e) => setClickId(e.currentTarget.value)}
 data-testid="chartboost-integration-click-id-input"
 />
 </div>

 <div className="flex flex-col gap-1.5">
 <label
 htmlFor="chartboost-integration-event-name"
 className={LABEL}
 >
 Event
 </label>
 <select
 id="chartboost-integration-event-name"
 className={SELECT}
 value={eventName}
 onChange={(e) => setEventName(e.currentTarget.value)}
 data-testid="chartboost-integration-event-name-input"
 >
 {EVENT_OPTIONS.map((opt) => (
 <option key={opt} value={opt}>
 {opt}
 </option>
 ))}
 </select>
 </div>

 <div className="flex flex-col gap-1.5">
 <label
 htmlFor="chartboost-integration-app-id"
 className={LABEL}
 >
 Network app id
 </label>
 <input
 id="chartboost-integration-app-id"
 type="text"
 required
 maxLength={128}
 placeholder="1234567890"
 className={INPUT}
 value={appId}
 onChange={(e) => setAppId(e.currentTarget.value)}
 data-testid="chartboost-integration-app-id-input"
 />
 </div>

 <div className="flex flex-col gap-1.5">
 <label htmlFor="chartboost-integration-ifa" className={LABEL}>
 Device IFA (optional)
 </label>
 <input
 id="chartboost-integration-ifa"
 type="text"
 maxLength={64}
 placeholder="GAID or IDFA"
 className={INPUT}
 value={ifa}
 onChange={(e) => setIfa(e.currentTarget.value)}
 data-testid="chartboost-integration-ifa-input"
 />
 </div>

 <div className="flex flex-col gap-1.5">
 <label
 htmlFor="chartboost-integration-country"
 className={LABEL}
 >
 Country (ISO-2, optional)
 </label>
 <input
 id="chartboost-integration-country"
 type="text"
 maxLength={2}
 minLength={0}
 placeholder="US"
 className={INPUT}
 value={country}
 onChange={(e) => setCountry(e.currentTarget.value)}
 data-testid="chartboost-integration-country-input"
 />
 </div>
 </div>

 <div className="flex items-center gap-3">
 <button
 type="submit"
 className={BTN}
 disabled={createMut.isPending}
 data-testid="chartboost-integration-save"
 >
 {createMut.isPending ? "Recording..." : "Record event"}
 </button>
 {saved ? (
 <p
 className="text-sm text-green-700"
 role="status"
 data-testid="chartboost-integration-saved"
 >
 Event recorded.
 </p>
 ) : null}
 {createMut.error ? (
 <p
 className="text-sm text-red-700"
 role="alert"
 data-testid="chartboost-integration-error"
 >
 {createMut.error.message}
 </p>
 ) : null}
 </div>
 </form>

 <section
 aria-label="recent events"
 className={`${CARD} flex flex-col gap-3`}
 data-testid="chartboost-integration-feed"
 >
 <h2 className="text-green-700 font-semibold text-lg font-semibold text-zinc-900">Recent events</h2>
 {listQ.isPending ? (
 <p
 className="text-sm text-zinc-600"
 data-testid="chartboost-integration-loading"
 >
 Loading...
 </p>
 ) : listQ.error ? (
 <p
 className="text-sm text-red-700"
 role="alert"
 data-testid="chartboost-integration-feed-error"
 >
 {listQ.error.message}
 </p>
 ) : items.length === 0 ? (
 <p
 className="text-sm text-zinc-600"
 data-testid="chartboost-integration-empty"
 >
 No postback events received yet. Submit a test event above or point
 the Chartboost network at POST /api/v1/chartboost-integration.
 </p>
 ) : (
 <ul
 className="flex flex-col divide-y divide-zinc-200"
 data-testid="chartboost-integration-feed-list"
 >
 {items.map((row) => (
 <li
 key={row.id}
 className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between"
 data-testid={`chartboost-integration-feed-row-${row.id}`}
 >
 <div className="flex flex-col gap-0.5">
 <span className="font-mono text-sm text-zinc-900">
 {maskId(row.chartboost_click_id)}
 </span>
 <span className="text-xs text-zinc-500">
 app {row.network_app_id}
 {row.country ? ` . ${row.country}` : ""}
 {row.ifa ? ` . ${maskId(row.ifa)}` : ""}
 </span>
 </div>
 <div className="flex items-center gap-3 text-xs">
 <span className="rounded-full border border-green-700 bg-green-50 px-2 py-0.5 font-medium text-green-700">
 {row.event_name}
 </span>
 <span
 className={
 row.forwarded
 ? "rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-zinc-600"
 : "rounded-full border border-green-700 bg-green-50 px-2 py-0.5 text-green-700"
 }
 >
 {row.forwarded ? "forwarded" : "queued"}
 </span>
 <span className="text-zinc-500">
 {fmtTs(row.created_at)}
 </span>
 </div>
 </li>
 ))}
 </ul>
 )}
 </section>
 </main>
	);
}

export default ChartboostIntegrationPage;
